import { computed } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useRoute, useRouter } from 'dashboard/composables/route';
import { goToStepRoute } from 'dashboard/helper/onboardingGuide/navigation';

/**
 * Entry point for starting and stopping onboarding guides, e.g. from the
 * "Start" button on the onboarding list.
 * @returns {{startGuide: Function, exitGuide: Function, isGuideActive: import('vue').ComputedRef<boolean>}}
 */
export function useOnboardingGuide() {
  const store = useStore();
  const route = useRoute();
  const router = useRouter();
  const accountId = useMapGetter('getCurrentAccountId');
  const isGuideActive = useMapGetter('onboardingGuide/isGuideActive');

  /**
   * Activates a guide and takes the user to its first step's page.
   * @param {string} guideId id from helper/onboardingGuide/registry.js
   * @returns {Promise<boolean>} false when the guide is unknown
   */
  const startGuide = async guideId => {
    const started = await store.dispatch('onboardingGuide/startGuide', guideId);
    if (!started) return false;
    await goToStepRoute({
      router,
      route,
      step: store.getters['onboardingGuide/activeStep'],
      accountId: accountId.value,
    });
    return true;
  };

  const exitGuide = () => store.dispatch('onboardingGuide/endGuide', 'exited');

  return {
    startGuide,
    exitGuide,
    isGuideActive: computed(() => isGuideActive.value),
  };
}
